/**
 * Enterprise-style client-side password rules (enforce again on the server).
 * - No substring of email local-part, username, or full-name tokens (min length 3).
 * - No run of 3+ digits that is strictly ascending, strictly descending, or all the same digit.
 */

const MIN_FORBIDDEN_LEN = 3;
const MIN_DIGIT_RUN = 3;

function normalizeForMatch(s) {
    return String(s || '')
        .normalize('NFKC')
        .trim()
        .toLowerCase();
}

/**
 * @typedef {{ email?: string, username?: string, fullName?: string }} PasswordPolicyContext
 */

export function buildPasswordPolicyContext({ email, username, fullName } = {}) {
    return {
        email: email != null ? String(email) : '',
        username: username != null ? String(username) : '',
        fullName: fullName != null ? String(fullName) : '',
    };
}

function collectForbiddenSubstrings(ctx) {
    const out = new Set();
    const add = (raw) => {
        const n = normalizeForMatch(raw);
        if (n.length >= MIN_FORBIDDEN_LEN) out.add(n);
    };

    const { email, username, fullName } = buildPasswordPolicyContext(ctx);

    if (email) {
        const at = email.indexOf('@');
        if (at > 0) add(email.slice(0, at));
    }
    if (username) add(username);
    if (fullName) {
        fullName.split(/[\s\-'.]+/).forEach((part) => add(part));
    }
    return [...out];
}

export function passwordContainsPersonalIdentifiers(password, context) {
    const p = normalizeForMatch(password);
    if (!p) return false;
    const subs = collectForbiddenSubstrings(context || {});
    return subs.some((sub) => p.includes(sub));
}

function quadAscending(digits) {
    for (let i = 1; i < digits.length; i++) {
        if (digits.charCodeAt(i) !== digits.charCodeAt(i - 1) + 1) return false;
    }
    return true;
}

function quadDescending(digits) {
    for (let i = 1; i < digits.length; i++) {
        if (digits.charCodeAt(i) !== digits.charCodeAt(i - 1) - 1) return false;
    }
    return true;
}

function quadAllSame(digits) {
    return /^(\d)\1+$/.test(digits);
}

export function passwordHasDisallowedDigitPattern(password) {
    const s = String(password || '');
    let i = 0;
    while (i < s.length) {
        if (!/\d/.test(s[i])) {
            i += 1;
            continue;
        }
        let j = i;
        while (j < s.length && /\d/.test(s[j])) j += 1;
        const segment = s.slice(i, j);
        if (segment.length >= MIN_DIGIT_RUN) {
            for (let k = 0; k <= segment.length - MIN_DIGIT_RUN; k += 1) {
                const chunk = segment.slice(k, k + MIN_DIGIT_RUN);
                if (quadAscending(chunk) || quadDescending(chunk) || quadAllSame(chunk)) {
                    return true;
                }
            }
        }
        i = j;
    }
    return false;
}

export const PASSWORD_CONTAINS_PERSONAL_INFO =
    'Do not use your name, username, or email in your password.';

export const PASSWORD_DISALLOWED_NUMBER_PATTERN =
    'Avoid obvious number patterns (e.g. 123, 1234, 4321, or repeated digits like 111).';

/**
 * @param {string} password
 * @param {PasswordPolicyContext | null | undefined} context
 * @returns {string | null} User-facing message or null if this layer passes.
 */
export function getPasswordPolicyViolationMessage(password, context) {
    const p = String(password || '');
    if (!p) return null;

    const ctx = buildPasswordPolicyContext(context || {});
    const hasPersonal = ctx.email || ctx.username || ctx.fullName;
    if (hasPersonal && passwordContainsPersonalIdentifiers(p, ctx)) {
        return PASSWORD_CONTAINS_PERSONAL_INFO;
    }
    if (passwordHasDisallowedDigitPattern(p)) {
        return PASSWORD_DISALLOWED_NUMBER_PATTERN;
    }
    return null;
}
